import type * as zod from 'zod';

import {
  CustomerFeatureFlagDataZod,
  customerFeatureFlagDefinitions,
  customerFeatureFlags,
} from './customerFeatureFlags';
import type { CustomerFeatureFlagDefinitionSchema } from './customerFeatureFlags';
import type { DevelopmentFeatureFlagDefinitionSchema } from './developmentFeatureFlags';
import {
  DevelopmentFeatureFlagDataZod,
  developmentFeatureFlagDefinitions,
  underDevelopmentFeatureFlags,
} from './developmentFeatureFlags';
import type { InternalFeatureFlagDefinitionSchema } from './internalFeatureFlags';
import {
  InternalFeatureFlagDataZod,
  internalFeatureFlagDefinitions,
  internalTestingFeatureFlags,
} from './internalFeatureFlags';

// Consolidated zod schema for feathers-bot service.
//
// No key may be declared in more than one of the three schemas — a test in
// featureFlags.test.ts enforces that. The merge order encodes the precedence
// that would apply if one ever slipped through: `.merge()` is last-wins, so
// customer outranks internal, which outranks development.
export const FeatureFlagDataZod = DevelopmentFeatureFlagDataZod.merge(
  InternalFeatureFlagDataZod,
).merge(CustomerFeatureFlagDataZod);

// Consolidated types
export type FeatureFlagData = zod.infer<typeof FeatureFlagDataZod>;
export type FeatureFlagKey = keyof FeatureFlagData;

// Section type shared across all feature flag categories
export type Section =
  | 'vision'
  | 'movement'
  | 'equipment'
  | 'infra'
  | 'safety'
  | 'newUI'
  | 'AI'
  | 'other'
  | 'environmentAndSpace';

// Base definition for all feature flags
export type BaseFeatureFlagDefinition = {
  title: string;
  dateAdded: string; // ISO date (YYYY-MM-DD) the flag was introduced, so its age is visible at a glance
  description?: string;
  allowSingleUser?: boolean; // allow to be used by one user - only works for browser based flags
  mutuallyExclusiveWith?: string; // if set, this flag cannot be enabled at the same time as the specified flag (must be a valid FeatureFlagKey)
  requiresRestart?: boolean; // set false only when every consumer hot-reloads; absent ⇒ restart required
};

// Category type
export type Category = 'customer' | 'internal' | 'developing';

// Consolidated definition schema
export type FeatureFlagDefinitionSchema = (
  | CustomerFeatureFlagDefinitionSchema
  | DevelopmentFeatureFlagDefinitionSchema
  | InternalFeatureFlagDefinitionSchema
) & {
  category: Category;
  section?: Section;
};

// Consolidated feature flag definitions. Spread in the same precedence order
// as FeatureFlagDataZod above, so a flag's category cannot disagree with the
// schema its default came from.
export const featureFlagDefinitions: Record<
  string,
  FeatureFlagDefinitionSchema
> = {
  ...Object.fromEntries(
    Object.entries(developmentFeatureFlagDefinitions).map(([key, def]) => [
      key,
      { ...def, category: 'developing' as const },
    ]),
  ),
  ...Object.fromEntries(
    Object.entries(internalFeatureFlagDefinitions).map(([key, def]) => [
      key,
      { ...def, category: 'internal' as const },
    ]),
  ),
  ...Object.fromEntries(
    Object.entries(customerFeatureFlagDefinitions).map(([key, def]) => [
      key,
      { ...def, category: 'customer' as const },
    ]),
  ),
};

/**
 * Default feature flag values for Thor robots.
 *
 * Written to feature_flags.json when the file is missing (e.g. after factory
 * reset via "Reset user data"). Once written, they behave like any other
 * user-set flag. All flags that should be ON for Thor are listed explicitly
 * so the config is self-documenting and resilient to schema default changes.
 */
export const THOR_FLAG_DEFAULTS: Partial<Record<FeatureFlagKey, boolean>> = {
  // Customer
  legacyHaasSteps: true,
  etherNetIP: true,
  ros2: true,
  // Under Development — Movement
  batchJogDirectionsValidation: true,
  enableOptimizerLinearPlanner: true,
  disableWeldWeaving: true,
  disableArcStepInWeld: true,
  // Under Development — Safety
  treatPRCSyncAndInterpolatorErrorsAsWarnings: true,
  // Internal Testing — Movement
  doNotResetPositionOnTrajectoryReset: true,
  // Internal Testing — Safety
  enableJointStoppingAccelerations: true,
  disableErrorOnInvalidTrajectory: true,
};

/**
 * The value every flag is expected to hold on a robot nobody has touched.
 *
 * Schema defaults, with THOR_FLAG_DEFAULTS layered on top when `isThor`,
 * because those flags are seeded into feature_flags.json on that model and so
 * are the untouched state there. Comparing against the schema alone would
 * report every one of them as an override on every Thor.
 *
 * Takes `isThor` rather than a model type because Thor is the only model with
 * an overlay, and because this module cannot import the model enum.
 *
 * Returns a fresh object per call, so a caller can mutate the result safely.
 */
export function getDefaultFlagValues(isThor: boolean): FeatureFlagData {
  const schemaDefaults = FeatureFlagDataZod.parse({});

  if (!isThor) {
    return schemaDefaults;
  }

  return { ...schemaDefaults, ...THOR_FLAG_DEFAULTS };
}

// Restart required unless the flag's definition explicitly opts out; unknown keys default to required.
export function flagRequiresRestart(key: FeatureFlagKey): boolean {
  return featureFlagDefinitions[key]?.requiresRestart !== false;
}

// Re-export category-specific grouped flags
export {
  customerFeatureFlags,
  internalTestingFeatureFlags,
  underDevelopmentFeatureFlags,
};
